// v10/src/components/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  ClipboardCheck, TrendingUp, AlertTriangle, CheckCircle2,
  RefreshCw, Clock, X, HelpCircle, BookOpen,
  Mic, CheckSquare, AlertCircle as AlertCircleIcon,
  Plane, Shield, Wifi, WifiOff, Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiService';

const C = {
  blue:   '#3b82f6', green:  '#22c55e', orange: '#f59e0b',
  red:    '#ef4444', purple: '#8b5cf6', cyan:   '#06b6d4', pink: '#ec4899',
};
const PIE_COLORS = [C.green, C.orange, C.red, C.purple];
const BAR_COLORS = [C.blue, C.purple, C.cyan, C.green, C.orange, C.pink];

const fmt = n => (n == null ? '—' : Number(n).toLocaleString('es-AR'));
const pct = n => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

const WELCOME_KEY = 'condor360_welcome_dismissed';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ── Welcome / Guía Card ───────────────────────────────────────────
const WelcomeCard = ({ onClose, darkMode }) => {
  const { t } = useTranslation('admin');

  const tips = [
    { icon: Mic,             color: C.purple, title: t('welcome_tip_voice_title'),     desc: t('welcome_tip_voice_desc') },
    { icon: CheckSquare,     color: C.green,  title: t('welcome_tip_check_title'),     desc: t('welcome_tip_check_desc') },
    { icon: AlertCircleIcon, color: C.orange, title: t('welcome_tip_pending_title'),   desc: t('welcome_tip_pending_desc') },
    { icon: TrendingUp,      color: C.blue,   title: t('welcome_tip_dashboard_title'), desc: t('welcome_tip_dashboard_desc') },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -16, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      style={{
        borderRadius: '24px', overflow: 'hidden', marginBottom: '28px',
        background: darkMode
          ? 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.14))'
          : 'linear-gradient(135deg, rgba(239,246,255,1), rgba(245,243,255,1))',
        border: darkMode ? '1px solid rgba(99,102,241,0.25)' : '1px solid rgba(199,210,254,0.8)',
        boxShadow: darkMode ? 'none' : '0 4px 24px rgba(99,102,241,0.08)',
      }}
    >
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)' }} />
      <div style={{ padding: '20px 22px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '14px', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BookOpen style={{ width: '20px', height: '20px', color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: darkMode ? 'white' : '#1e1b4b' }}>
                {t('welcome_title')}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6366f1' }}>
                {t('welcome_subtitle')}
              </p>
            </div>
          </div>
          <motion.button onClick={onClose} whileTap={{ scale: 0.9 }}
            style={{ padding: '6px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.1)', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X style={{ width: '14px', height: '14px' }} />
          </motion.button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
          {tips.map((tip, i) => {
            const Icon = tip.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
                style={{ display: 'flex', gap: '10px', padding: '12px', borderRadius: '14px', background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)', border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(199,210,254,0.6)' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: `${tip.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon style={{ width: '15px', height: '15px', color: tip.color }} />
                </div>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.85)' : '#1e1b4b' }}>{tip.title}</p>
                  <p style={{ margin: 0, fontSize: '11px', lineHeight: 1.45, color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b' }}>{tip.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <p style={{ margin: '14px 0 0', fontSize: '11px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
          {t('welcome_footer')}
        </p>
      </div>
    </motion.div>
  );
};

// ── Stat card ─────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color, delay, darkMode }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    style={{
      background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
      borderRadius: '20px', padding: '16px 18px',
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.05)',
    }}
  >
    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon style={{ width: '18px', height: '18px', color }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b' }}>{label}</p>
      <p style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a', lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ margin: '3px 0 0', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>{sub}</p>}
    </div>
  </motion.div>
);

const SectionTitle = ({ children, darkMode }) => (
  <h3 style={{ margin: '24px 0 12px', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
    {children}
  </h3>
);

const ChartCard = ({ title, children, darkMode, style = {} }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
    style={{
      background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
      borderRadius: '20px', padding: '16px 18px',
      boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.05)',
      minWidth: 0,
      ...style,
    }}
  >
    {title && <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>{title}</p>}
    {children}
  </motion.div>
);

const CustomTooltip = ({ active, payload, label, darkMode }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: darkMode ? '#1e293b' : 'white', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
      <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0', fontSize: '13px', fontWeight: 700, color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ── Live Activity Panel ───────────────────────────────────────────
const LiveActivityPanel = ({ darkMode }) => {
  const { t, i18n } = useTranslation('admin');
  const [liveData, setLiveData] = useState(null);
  const [lastPing, setLastPing] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const fetchLive = async () => {
    try {
      const [inspRes, squawkRes, authRes] = await Promise.allSettled([
        api.get('/api/inspections?status=in_progress&limit=5'),
        api.get('/api/squawks?status=open&limit=10'),
        api.get('/api/flight-auth/pending'),
      ]);
      const activeInspections = inspRes.status === 'fulfilled' ? (inspRes.value.data?.data || []) : [];
      const aogSquawks = squawkRes.status === 'fulfilled' ? (squawkRes.value.data?.data || []).filter(s => s.status === 'open' || s.status === 'in_progress') : [];
      const pendingAuths = authRes.status === 'fulfilled' ? (authRes.value.data?.count || 0) : 0;
      setLiveData({ activeInspections, aogSquawks, pendingAuths });
      setLastPing(new Date());
    } catch (_) {}
  };

  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 15000);
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(interval);
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const cardBg     = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const locale     = i18n.language === 'en' ? 'en-US' : 'es-AR';

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '24px', background: cardBg, border: cardBorder, boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.05)' }}
    >
      <div style={{ padding: '12px 18px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <motion.div
            animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }}
            style={{ width: '7px', height: '7px', borderRadius: '50%', background: isOnline ? '#22c55e' : '#94a3b8', boxShadow: isOnline ? '0 0 6px rgba(34,197,94,0.6)' : 'none' }}
          />
          <span style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
            {t('live_status')}
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {lastPing && (
            <span style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>
              {lastPing.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          {isOnline
            ? <Wifi style={{ width: '12px', height: '12px', color: '#22c55e' }} />
            : <WifiOff style={{ width: '12px', height: '12px', color: '#94a3b8' }} />
          }
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', alignItems: 'start' }}>
        {/* En chequeo */}
        <div style={{ padding: '14px 16px', background: cardBg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', height: '36px' }}>
            <Activity style={{ width: '13px', height: '13px', color: '#3b82f6', flexShrink: 0 }} />
            <span style={{ fontSize: '10px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
              {t('live_checking')}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 900, color: '#3b82f6', lineHeight: 1 }}>
            {liveData ? liveData.activeInspections.length : '—'}
          </p>
          {liveData && liveData.activeInspections.length > 0 && (
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {liveData.activeInspections.slice(0, 2).map((insp, i) => (
                <div key={i} style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Plane style={{ width: '9px', height: '9px', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {insp.registration || insp.model_name} · {insp.inspector_name?.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Squawks */}
        <div style={{ padding: '14px 16px', background: cardBg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', height: '36px' }}>
            <AlertTriangle style={{ width: '13px', height: '13px', color: liveData && liveData.aogSquawks.some(s => s.severity === 'aog') ? '#ef4444' : liveData && liveData.aogSquawks.length > 0 ? '#f59e0b' : '#94a3b8' }} />
            <span style={{ fontSize: '10px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
              {t('live_squawks')}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 900, color: liveData && liveData.aogSquawks.some(s => s.severity === 'aog') ? '#ef4444' : liveData && liveData.aogSquawks.length > 0 ? '#f59e0b' : (darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8'), lineHeight: 1 }}>
            {liveData ? liveData.aogSquawks.length : '—'}
          </p>
          {liveData && liveData.aogSquawks.length > 0 && (
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {liveData.aogSquawks.slice(0, 2).map((sq, i) => (
                <div key={i} style={{ fontSize: '10px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sq.registration || sq.aircraft_name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Auth pendientes */}
        <div style={{ padding: '14px 16px', background: cardBg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', height: '36px' }}>
            <Shield style={{ width: '13px', height: '13px', color: liveData && liveData.pendingAuths > 0 ? '#f59e0b' : '#94a3b8' }} />
            <span style={{ fontSize: '10px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.4)' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
              {t('live_auth_pending')}
            </span>
          </div>
          <motion.p
            animate={liveData && liveData.pendingAuths > 0 ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{ margin: 0, fontSize: '28px', fontWeight: 900, color: liveData && liveData.pendingAuths > 0 ? '#f59e0b' : (darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8'), lineHeight: 1 }}>
            {liveData ? liveData.pendingAuths : '—'}
          </motion.p>
          {liveData && liveData.pendingAuths > 0 && (
            <p style={{ margin: '6px 0 0', fontSize: '10px', color: '#d97706', fontWeight: 600 }}>
              {t('live_requires_attention')}
            </p>
          )}
        </div>
      </div>

      <div style={{ padding: '8px 18px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}>
          <RefreshCw style={{ width: '10px', height: '10px', color: darkMode ? 'rgba(255,255,255,0.2)' : '#94a3b8' }} />
        </motion.div>
        <span style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.2)' : '#94a3b8' }}>
          {t('live_auto_update')}
        </span>
      </div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const AdminDashboard = ({ darkMode, guideKey = 0 }) => {
  const { t, i18n } = useTranslation('admin');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem(WELCOME_KEY));

  const isMobile = useIsMobile();
  const locale   = i18n.language === 'en' ? 'en-US' : 'es-AR';

  const handleCloseWelcome = () => {
    localStorage.setItem(WELCOME_KEY, '1');
    setShowWelcome(false);
  };

  useEffect(() => {
    if (guideKey > 0) {
      setShowWelcome(true);
      localStorage.removeItem(WELCOME_KEY);
    }
  }, [guideKey]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/dashboard');
      setData(res.data.data);
      setLastUpdate(new Date());
    } catch {
      setError(t('error_metrics'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const text = darkMode ? 'rgba(255,255,255,0.85)' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)'  : '#64748b';
  const axis = darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8';

  const chartH      = isMobile ? 160 : 200;
  const chartHSmall = isMobile ? 140 : 160;
  const pieH        = isMobile ? 160 : 180;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        style={{ width: '36px', height: '36px', border: `3px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, borderTopColor: C.blue, borderRadius: '50%' }} />
      <p style={{ color: sub, fontSize: '14px' }}>{t('loading_metrics')}</p>
    </div>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: C.red, background: 'rgba(239,68,68,0.06)', borderRadius: '20px', border: '1px solid rgba(239,68,68,0.15)' }}>
      <AlertTriangle style={{ width: '40px', height: '40px', margin: '0 auto 12px', display: 'block' }} />
      {error}
      <br />
      <motion.button onClick={fetchData} whileTap={{ scale: 0.95 }}
        style={{ marginTop: '16px', padding: '10px 20px', background: C.red, color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <RefreshCw style={{ width: '14px', height: '14px' }} /> {t('retry')}
      </motion.button>
    </div>
  );

  const { summary, last30Days, topSkippedItems, topPilots, byAircraft, observationsByPilot, completionByHour } = data;

  const pieData = [
    { name: t('stat_completed'), value: parseInt(summary.completed  || 0) },
    { name: t('stat_incomplete', { count: '' }).replace(' ', '').replace('incomp.', 'Incompletas').replace('incompl.', 'Incomplete'), value: parseInt(summary.incomplete || 0) },
  ].filter(d => d.value > 0);

  const hourData = Array.from({ length: 24 }, (_, h) => {
    const found = completionByHour.find(r => parseInt(r.hour) === h);
    return { hour: `${String(h).padStart(2,'0')}h`, total: found ? parseInt(found.total) : 0 };
  }).filter(d => d.total > 0);

  return (
    <div>
      <AnimatePresence>
        {showWelcome && <WelcomeCard onClose={handleCloseWelcome} darkMode={darkMode} />}
      </AnimatePresence>

      <LiveActivityPanel darkMode={darkMode} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TrendingUp style={{ width: '22px', height: '22px', color: 'white' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: text }}>{t('dashboard_title')}</h2>
          <p style={{ margin: 0, fontSize: '12px', color: sub }}>
            {t('dashboard_subtitle')}
            {lastUpdate ? ` · ${t('updated_at', { time: lastUpdate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) })}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <motion.button onClick={() => window.dispatchEvent(new CustomEvent('condor360:open-assistant'))} whileTap={{ scale: 0.93 }}
            title={t('view_guide')}
            style={{ padding: '10px', background: darkMode ? 'rgba(99,102,241,0.15)' : '#ede9fe', border: 'none', borderRadius: '12px', cursor: 'pointer', color: '#8b5cf6', display: 'flex', alignItems: 'center' }}>
            <HelpCircle style={{ width: '15px', height: '15px' }} />
          </motion.button>
          <motion.button onClick={fetchData} whileTap={{ scale: 0.93 }}
            style={{ padding: '10px', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', color: sub, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600 }}>
            <RefreshCw style={{ width: '15px', height: '15px' }} />
            {!isMobile && t('refresh')}
          </motion.button>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
        <StatCard icon={ClipboardCheck} label={t('stat_total')}          value={fmt(summary.total)}               sub={t('stat_last_30d', { count: fmt(summary.last_30_days) })}   color={C.blue}   delay={0.05} darkMode={darkMode} />
        <StatCard icon={CheckCircle2}   label={t('stat_completed')}      value={fmt(summary.completed)}           sub={t('stat_incomplete', { count: fmt(summary.incomplete) })}    color={C.green}  delay={0.10} darkMode={darkMode} />
        <StatCard icon={TrendingUp}     label={t('stat_completion_pct')} value={pct(summary.avg_completion_pct)} sub={t('stat_avg')}                                                 color={C.purple} delay={0.15} darkMode={darkMode} />
        <StatCard icon={Clock}          label={t('stat_last_week')}      value={fmt(summary.last_7_days)}         sub={t('stat_inspections')}                                         color={C.orange} delay={0.20} darkMode={darkMode} />
      </div>

      {/* Tendencia */}
      {last30Days.length > 0 && (
        <>
          <SectionTitle darkMode={darkMode}>{t('section_activity')}</SectionTitle>
          <ChartCard darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={chartH}>
              <LineChart data={last30Days} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Line type="monotone" dataKey="total"     name={t('chart_total')}     stroke={C.blue}  strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="completed" name={t('chart_completed')} stroke={C.green} strokeWidth={2}   dot={false} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}

      {/* Estado general */}
      <SectionTitle darkMode={darkMode}>{t('section_status')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,2fr)', gap: '10px' }}>
        {pieData.length > 0 && (
          <ChartCard title={t('chart_completed_vs_incomplete')} darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={pieH}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={isMobile ? 38 : 45} outerRadius={isMobile ? 60 : 72} paddingAngle={3} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', color: sub }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {topSkippedItems.length > 0 && (
          <ChartCard title={t('chart_top_skipped')} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: isMobile ? '180px' : '200px', overflowY: 'auto' }}>
              {topSkippedItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '18px', fontSize: '11px', fontWeight: 800, color: i < 3 ? C.red : sub, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.item_text}</p>
                    <p style={{ margin: 0, fontSize: '10px', color: sub }}>{item.section_name}</p>
                  </div>
                  <span style={{ padding: '2px 7px', borderRadius: '8px', background: i < 3 ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.1)', color: i < 3 ? C.red : sub, fontSize: '11px', fontWeight: 800, flexShrink: 0 }}>
                    {item.times_skipped}×
                  </span>
                </div>
              ))}
            </div>
          </ChartCard>
        )}
      </div>

      {/* Pilotos & aeronaves */}
      <SectionTitle darkMode={darkMode}>{t('section_pilots_aircraft')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '10px' }}>
        {topPilots.length > 0 && (
          <ChartCard title={t('chart_top_pilots')} darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={chartH}>
              <BarChart
                data={topPilots.map(p => ({ name: p.pilot_name.split(' ')[0], total: parseInt(p.total), pct: parseInt(p.avg_pct || 0) }))}
                margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
              >
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Bar dataKey="total" name={t('chart_inspections')} radius={[6,6,0,0]}>
                  {topPilots.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {byAircraft.length > 0 && (
          <ChartCard title={t('chart_by_aircraft')} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {byAircraft.map((a, i) => {
                const pctNum = parseInt(a.avg_pct || 0);
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ minWidth: 0, flex: 1, marginRight: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: text }}>{a.registration || a.aircraft}</span>
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: sub }}>{a.total} {t('insp_label')}</span>
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: pctNum >= 80 ? C.green : pctNum >= 60 ? C.orange : C.red, flexShrink: 0 }}>{pctNum}%</span>
                    </div>
                    <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }} animate={{ width: `${pctNum}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1, ease: 'easeOut' }}
                        style={{ height: '100%', borderRadius: '3px', background: pctNum >= 80 ? C.green : pctNum >= 60 ? C.orange : C.red }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        )}
      </div>

      {/* Observaciones & horario */}
      {(observationsByPilot.length > 0 || hourData.length > 0) && (
        <>
          <SectionTitle darkMode={darkMode}>{t('section_observations')}</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '10px' }}>
            {observationsByPilot.length > 0 && (
              <ChartCard title={t('chart_obs_by_pilot')} darkMode={darkMode}>
                <ResponsiveContainer width="100%" height={chartHSmall}>
                  <BarChart
                    data={observationsByPilot.map(o => ({ name: o.pilot_name.split(' ')[0], obs: parseInt(o.observations) }))}
                    margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                  >
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <Bar dataKey="obs" name={t('chart_observations')} fill={C.orange} radius={[6,6,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
            {hourData.length > 0 && (
              <ChartCard title={t('chart_by_hour')} darkMode={darkMode}>
                <ResponsiveContainer width="100%" height={chartHSmall}>
                  <BarChart data={hourData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <XAxis dataKey="hour" tick={{ fontSize: 9, fill: axis }} tickLine={false} axisLine={false} interval={isMobile ? 3 : 1} />
                    <YAxis tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                    <Bar dataKey="total" name={t('chart_inspections')} fill={C.cyan} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}

      {/* Tabla pilotos */}
      {topPilots.length > 0 && (
        <>
          <SectionTitle darkMode={darkMode}>{t('section_pilot_detail')}</SectionTitle>
          <ChartCard darkMode={darkMode}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: isMobile ? '400px' : 'unset' }}>
                <thead>
                  <tr style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9' }}>
                    {[t('table_pilot'), t('table_total'), t('table_completed'), t('table_pct')].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: sub, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topPilots.map((p, i) => {
                    const pctNum = parseInt(p.avg_pct || 0);
                    return (
                      <tr key={i} style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f8fafc' }}>
                        <td style={{ padding: '10px 10px', fontWeight: 700, color: text, whiteSpace: 'nowrap' }}>{p.pilot_name}</td>
                        <td style={{ padding: '10px 10px', color: sub }}>{p.total}</td>
                        <td style={{ padding: '10px 10px', color: C.green, fontWeight: 700 }}>{p.completed}</td>
                        <td style={{ padding: '10px 10px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '8px', fontWeight: 800, fontSize: '12px', background: pctNum >= 80 ? 'rgba(34,197,94,0.1)' : pctNum >= 60 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)', color: pctNum >= 80 ? C.green : pctNum >= 60 ? C.orange : C.red }}>
                            {pctNum}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
